import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, Subscription, throttleTime } from 'rxjs';
import { SharedCaseManagementService } from '../../shared/services/shared-case-management.service';
import { Router } from '@angular/router';
import { CaseManagementService } from '../../service/case-management.service';
import { ToastrService } from 'ngx-toastr';
import { DateTime } from 'luxon';
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS, MAT_LUXON_DATE_FORMATS } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatTooltipModule, ScrollingModule, MatProgressSpinnerModule],
  providers: [
    { provide: DateAdapter, useClass: LuxonDateAdapter, deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
  ],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit, OnChanges {
  private notificationsSub?: Subscription;
  notifications: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  loadingNotifications = false;
  page: number = 0;
  limit: number = 10;
  totalPages: number = 0;
  constructor(private readonly sharedCaseManagementService: SharedCaseManagementService, private readonly router: Router, private readonly caseManagementService: CaseManagementService, private readonly cdr: ChangeDetectorRef,
    private readonly toastr: ToastrService
  ) {}
  
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport | undefined;

  ngOnInit() {
    this.notificationsSub = this.notifications.subscribe(list => {
      if(!localStorage.getItem('red_notification_dot')) {
        localStorage.setItem('red_notification_dot', 'false');
        this.sharedCaseManagementService.setRedNotificationDotVisibility(false);
      }
      if(localStorage.getItem('red_notification_dot') === 'true') {
        this.sharedCaseManagementService.setRedNotificationDotVisibility(true);
      }
      else {
        localStorage.setItem('red_notification_dot', 'false');
        this.sharedCaseManagementService.setRedNotificationDotVisibility(false);
      }
    });
    this.sharedCaseManagementService.setRedNotificationDotVisibility(localStorage.getItem('red_notification_dot') === 'true');
    this.sharedCaseManagementService.addNotification$.subscribe((notification: any) => {
      if (notification) {
          this.sharedCaseManagementService.setRedNotificationDotVisibility(true);
          localStorage.setItem('red_notification_dot', 'true');
          this.page = 0;
          this.notifications.next([]);
          this.fetchAllNotifications();
      }
    });
    this.sharedCaseManagementService.notificationClicked$.subscribe((notification: any) => {
      this.page = 0;
      this.notifications.next([]);
      if (notification) {
        this.fetchAllNotifications();
      }
    });
  }
  ngAfterViewInit() {
    if (this.viewport) {
      this.viewport
        .elementScrolled()
        .pipe(throttleTime(200))
        .subscribe(() => {
          this.checkIfShouldLoad();
        });
    }
  }
  checkIfShouldLoad() {
    if (!this.viewport || this.  loadingNotifications) return;
  
    const endReached = this.viewport.getRenderedRange().end >= this.viewport.getDataLength();
    const scrollOffset = this.viewport.measureScrollOffset('bottom');
  
    // Trigger load if either user reached end of list OR near bottom
    if (endReached || scrollOffset <= 10) {
      if(this.page< this.totalPages - 1) {
        this.page = this.page + 1;
        this.fetchAllNotifications();
      }
    }
  }
  fetchAllNotifications() {
    this.loadingNotifications = true;
    this.cdr.detectChanges();
    this.caseManagementService.getAllNotifications(this.page, this.limit).subscribe({
      next: (newNotifications: any) => {
        if(newNotifications?.content.length > 0) {
          let combinedNotifications = [...this.notifications.value, ...newNotifications.content];
          this.totalPages = newNotifications?.totalPages || 0;
          combinedNotifications = combinedNotifications.map((notification: any) => {
            return {
              ...notification,
              updatedDate: DateTime.fromISO(notification?.updatedDate).toFormat('MM/dd/yyyy hh:mm:ss a'),
              isRead: notification.isRead || 0
            };
          });
          this.notifications.next(combinedNotifications);
          this.loadingNotifications = false;
          this.cdr.detectChanges();
        }
        else {
          this.loadingNotifications = false;
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        console.error('Error fetching notifications:', error);
        this.loadingNotifications = false;
        this.toastr.error('Error fetching notifications');
      }
    });
  }
  ngOnChanges(changes: SimpleChanges) {
  }
  viewCase(notification: any) {
    this.caseManagementService.viewCase(notification.deliveryId).subscribe({
      next: (response: any) => {
        if (response) {
          this.sharedCaseManagementService.changeCase(
          {
            applicantName: notification.applicantName,
            applicationEditable: notification.applicationEditable,
            assignee: notification.assignee,
            assigneeId: notification.assigneeId,
            caseAdminAllowed: notification.caseAdminAllowed,
            caseCompletionGroupStatus: notification.caseCompletionGroupStatus,
            caseCompletionStatus: notification.caseCompletionStatus,
            caseId: notification.caseId,
            caseStatusId: notification.caseStatusId,
            lastUpdatedDate: notification.lastUpdatedDate,
            processId: notification.processId,
            timeConsumedInPercentage: notification.timeConsumedInPercentage,
            timeRemaining: notification.timeRemaining
          }
          );
          this.sharedCaseManagementService.changeCaseView('view');
          this.router.navigate(['/case-detail'], {
            queryParams: { id: notification.caseId },
            state: { 'caseView': 'view' }
          });
        }
      },
      error: (error: any) => {
        console.error('Error viewing case:', error);
        this.toastr.error('Error viewing case');
      }
    });
  }
  ngOnDestroy() {
    this.notificationsSub?.unsubscribe();
  }
  closeNotificationPanel() {
    this.sharedCaseManagementService.setRedNotificationDotVisibility(false);
    localStorage.setItem('red_notification_dot', 'false');
    this.page = 0;
    this.notifications.next([]);
    this.loadingNotifications = false;
  }
}
